// components/InpaintCanvas.tsx
import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import WorkspaceCard from './WorkspaceCard';
import GeneratingLayers from './GeneratingLayers';
import {
  Layer,
  Rect,
  Workspace,
  KernelHandle,
  QuickMenuData,
  QuickMenuAction,
  LayerModificationLogEntry,
  LayerReclickPress,
  MaskShape,
} from  '#types/types';
import NewInpaintToolbar from './NewInpaintToolbar';
import type { AnnotationCanvasHandle } from './AnnotationCanvas';
import {
  useKernelStore,
} from '@/kernel/kernelStores';
import anime from 'animejs';
import BottomBar from './BottomBar';
import SubtleEnergyBackground, { SubtleEnergyBackgroundHandle } from './SubtleEnergyBackground';
import { DEFAULT_BACKDROP_THEME } from './layout/backdropTheme';
import GridGenerationInlinePanel from './GridGenerationInlinePanel';
import { generationAreaCursor, scissorsCursor, getMaskPenCursor } from '#lib/svg';
import {
  quoteSweepUpscaleCredits,
  type SweepUpscaleGridSize,
  type GridGenerationPayload,
} from '#lib/grid-generation-script';
import { prepareRebuildMessage, projectSurfaceOf, rememberedStreetCuts } from '#lib/modelist/projectScene';
import { isStreetPanoramaFrame, STREET_DEVELOP_RECIPES, STREET_REPAIR_RECIPE, STREET_TEXTURE_RECIPES, streetRepairErrors } from '#lib/modelist/streetDevelop';
import CreditsBadge from './ui/CreditsBadge';
import { quoteRecipeCredits } from './workspacePickOverlay';
import { RECAST_EXTRAS_RECIPE } from '#lib/recastExtras';
import { isLayerVectorized } from '#lib/svg-layer';
import { projectStudioLayerPointToLocal, resolveStudioLayerTransform } from '#lib/studio-layer-transform';
import { DEFAULT_STUDIO_DEVELOP_SETTINGS } from '#lib/studio-develop';
import { STUDIO_LIVE_VIEWPORT_VARS_RESET } from '#lib/studio-viewport';
import {
  DEFAULT_CANVAS_RESIZE_STATE,
  DEFAULT_FRAME_RESIZE_STATE,
  cloneLayerSnapshot,
} from '#lib/studio-runtime-dto';
import CanvasDrawingToolOverlay from './CanvasDrawingToolOverlay';
import WorkspaceColumnPickOverlay from './WorkspaceColumnPickOverlay';
import RecastZonePickOverlay from './RecastZonePickOverlay';
import { useCanvasDrawingToolController } from './useCanvasDrawingToolController';
import FloatingCanvasInput from './FloatingCanvasInput';
import {
  inpaintMaskHasContent,
  isPointInsideInpaintMask,
  type MaskPoint,
  worldPointToWorkspaceRasterPoint,
} from './inpaintMaskGeometry';
import {
  getProjectIsPanning,
  getProjectPanOffset,
  getProjectZoomLevel,
  getProjectDraggedLayerId,
  getProjectIsResizingLayer,
  getProjectPointBeingDragged,
  getProjectSelectedLayersId,
  setProjectIsPanning,
  setProjectPanOffset,
  setProjectZoomLevel,
  setProjectDraggedLayerId,
  setProjectIsResizingLayer,
  setProjectPointBeingDragged,
  setProjectRightSidebarTab,
  requestProjectLayerListReveal,
  isStationaryLayerReclick,
} from '#lib/project-runtime-layer-ui-state';

const translations = {
  en: {
    inpaint: 'Inpaint',
    inpaintPromptPlaceholder: 'Describe what should appear here',
    clearAnnotations: 'Clear Annotations',
    download: 'Export',
    exportImage: 'Image',
    exportVideo: 'Video',
    import: 'Import',
    switchTo: (name: string) => `Switch to "${name}"`,
    duplicateNamed: (name: string) => `Duplicate "${name}"`,
    deleteNamed: (name: string) => `Delete "${name}"`,
    addWorkspace: 'Add Workspace',
    powers: 'Powers',
    develop360: 'Develop 360',
    develop360Tooltip: 'Click on the panorama to cut its horizon into slices, add a few words per slice if you like, then develop: one generation per slice, all at once, adding each brand’s shop interiors and visitors. Texturise first.',
    setSeam: 'Set the seam…',
    setSeamTooltip: 'Click where the panorama’s left and right edges should meet. Escape cancels.',
    texturiseTooltip: 'Before Develop 360: one 4K generation over the whole panorama that only makes the render more photographic.',
    repairCutsTooltip: 'After Develop 360: one generation per cut, all at once, each told to hide a red stroke drawn on its cut; only a narrow strip over the cut is kept.',
    recastExtras: 'Recast extras…',
    recastExtrasTooltip: 'Finds the people in the frame and groups them into square zones. Click a zone to turn it on or off and add a few words, drag it to move it, drag on the photo to add one, then recast: one generation per zone, top to bottom.',
    recastGenerationArea: 'Recast extras here',
    recastGenerationAreaTooltip: 'One generation of the recast recipe on the generation area, to redo a person the zones missed.',
    stopRecastExtras: 'Stop recasting',
    stopRecastExtrasTooltip: 'Stops before the next zone: the zone being recast still lands.',
    filter: 'Filter',
    segment: 'Segment',
    segmentTooltip: 'Generates a new layer with the main subject of the image, without its background.',
    quickEnhance: '1K Enhance',
    quickUpscale: '1K Upscale',
    quickEnhanceTooltip: 'Run quick enhance on current generation area. Right-click for grid sweep configuration.',
    quickUpscaleTooltip: 'Run quick upscale on current generation area. Right-click for grid sweep configuration.',
    upscale8k: '8K Upscale',
    upscale8kTooltip: (credits: number | null) => (
      `A 2x2 SeedVR2 sweep at 28 Mpx per tile over the whole workspace.${credits === null ? '' : ` Costs ${credits} credits.`} Starts immediately, no panel.`
    ),
    upscale16k: '16K Upscale',
    upscale16kTooltip: (credits: number | null) => (
      `Last pass before delivery: a 3x3 SeedVR2 sweep at 32 Mpx per tile over the whole workspace, taking a 4k workspace to roughly 16k.${credits === null ? '' : ` Costs ${credits} credits.`} Starts immediately, no panel.`
    ),
    layerMenu: 'Layer',
    cut: 'Cut',
    harmonize: 'Harmonize',
    integrate: 'Integrate',
    blend: 'Feather Edges',
    renderCanvasToLayer: 'Create',
    vectorizeVisibleLayers: 'Vectorize',
    vectorizeVisibleLayersTooltip: 'Generates a new vector drawing layer.',
    rebuildIn3d: 'Rebuild in 3D…',
    rebuildIn3dTooltip: 'Opens the 3D scene on a message to the Modelist with this workspace attached. You say what to rebuild.',
  },
  fr: {
    inpaint: 'Inpaint',
    inpaintPromptPlaceholder: 'Décrivez ce qui doit apparaître ici',
    clearAnnotations: 'Effacer les annotations',
    download: 'Exporter',
    exportImage: 'Image',
    exportVideo: 'Vidéo',
    import: 'Importer',
    switchTo: (name: string) => `Basculer vers "${name}"`,
    duplicateNamed: (name: string) => `Dupliquer "${name}"`,
    deleteNamed: (name: string) => `Supprimer "${name}"`,
    addWorkspace: 'Ajouter un espace de travail',
    powers: 'Pouvoirs',
    develop360: 'Développer en 360',
    develop360Tooltip: 'Cliquez sur le panorama pour couper son horizon en tranches, ajoutez si vous voulez quelques mots par tranche, puis développez : une génération par tranche, toutes à la fois, qui ajoute l’intérieur des boutiques de chaque enseigne et des visiteurs. Texturisez d’abord.',
    setSeam: 'Placer la couture…',
    setSeamTooltip: 'Cliquez là où les bords gauche et droit du panorama doivent se rejoindre. Échap annule.',
    texturiseTooltip: 'Avant Développer en 360 : une génération 4K sur tout le panorama, qui ne fait que rendre le rendu plus photographique.',
    repairCutsTooltip: 'Après Développer en 360 : une génération par coupe, toutes à la fois, chacune chargée d’effacer un trait rouge tracé sur sa coupe ; seule une bande étroite sur la coupe est gardée.',
    recastExtras: 'Refaire les figurants…',
    recastExtrasTooltip: 'Trouve les personnes du cadre et les regroupe en zones carrées. Cliquez une zone pour l’activer ou la désactiver et ajouter quelques mots, glissez-la pour la déplacer, glissez sur la photo pour en ajouter une, puis lancez : une génération par zone, de haut en bas.',
    recastGenerationArea: 'Refaire les figurants ici',
    recastGenerationAreaTooltip: 'Une génération de la recette des figurants sur la zone de génération, pour reprendre une personne oubliée par les zones.',
    stopRecastExtras: 'Arrêter les figurants',
    stopRecastExtrasTooltip: 'S’arrête avant la zone suivante : la zone en cours arrive quand même.',
    filter: 'Filtrer',
    segment: 'Segment',
    segmentTooltip: 'Génère un nouveau calque avec le sujet principal de l’image, sans background.',
    quickEnhance: '1K Enhance',
    quickUpscale: '1K Upscale',
    quickEnhanceTooltip: 'Lance une amelioration rapide sur la zone de generation. Clic droit pour configurer le balayage en grille.',
    quickUpscaleTooltip: 'Lance un upscale rapide sur la zone de generation. Clic droit pour configurer le balayage en grille.',
    upscale8k: '8K Upscale',
    upscale8kTooltip: (credits: number | null) => (
      `Un balayage SeedVR2 2x2 à 28 Mpx par tuile sur tout le workspace.${credits === null ? '' : ` Coûte ${credits} crédits.`} Démarre immédiatement, sans panneau.`
    ),
    upscale16k: '16K Upscale',
    upscale16kTooltip: (credits: number | null) => (
      `Dernière passe avant livraison : un balayage SeedVR2 3x3 à 32 Mpx par tuile sur tout le workspace, qui emmène un workspace déjà en 4k vers environ 16k.${credits === null ? '' : ` Coûte ${credits} crédits.`} Démarre immédiatement, sans panneau.`
    ),
    layerMenu: 'Layer',
    cut: 'Découper',
    harmonize: 'Harmoniser',
    integrate: 'Intégrer',
    blend: 'Adoucir les bords',
    renderCanvasToLayer: 'Créer',
    vectorizeVisibleLayers: 'Vectorize',
    vectorizeVisibleLayersTooltip: 'Génère un nouveau calque en dessin vectoriel.',
    rebuildIn3d: 'Reconstruire en 3D…',
    rebuildIn3dTooltip: 'Ouvre la scène 3D sur un message au Modelist avec ce workspace en pièce jointe. Vous dites quoi reconstruire.',
  }
};

const cloneMaskShapesForUndo = (shapes: MaskShape[] | null | undefined): MaskShape[] =>
  (shapes ?? []).map((shape) => ({
    ...shape,
    points: shape.points.map((point) => ({ ...point })),
  }));

interface InpaintCanvasProps {
  kernel: React.RefObject<KernelHandle | null>;
  projectId: string;
}

type IdeogramInpaintPromptState = {
  workspaceId: string;
  canvasX: number;
  canvasY: number;
  maskPoint: MaskPoint;
  prompt: string;
  openedAt: number;
};

const InpaintCanvas = React.memo<InpaintCanvasProps>(({
  kernel,
  projectId
  }) => {

  const transformContainerRef = useRef<HTMLDivElement>(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const starfieldRef = useRef<SubtleEnergyBackgroundHandle | null>(null);

  const lang = useKernelStore((state) => state.language);
  const dbUserId = useKernelStore((state) => state.userContext.dbUserId);
  const activeTool = useKernelStore((state) => state.activeTool);
  const maskPenMode = useKernelStore((state) => state.maskPenMode);
  const isPanning = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    return runtime?.isPanning === true;
  });
  const workspaceIdsSignature = useKernelStore((state) => {
    const workspaceIds = state.inventory.Project.registry[projectId]?.workspaceIds;
    return Array.isArray(workspaceIds) ? workspaceIds.join(',') : '';
  });
  const workspaceIds = useMemo(
    () => (workspaceIdsSignature ? workspaceIdsSignature.split(',') : []),
    [workspaceIdsSignature]
  );
  const mountedWorkspaceIdsSignature = useKernelStore((state) => {
    const project = state.inventory.Project.registry[projectId];
    if (!Array.isArray(project?.workspaceIds)) return '';
    const runtimeWorkspaceId = typeof project.runtime?.UIWorkspaceId === 'string'
      ? project.runtime.UIWorkspaceId.trim()
      : '';
    const workspaceRegistry = state.inventory.StudioWorkspace.registry;
    return project.workspaceIds
      .filter((workspaceId) =>
        workspaceRegistry[workspaceId]?.visible !== false || workspaceId === runtimeWorkspaceId
      )
      .join(',');
  });
  const mountedWorkspaceIds = useMemo(
    () => (mountedWorkspaceIdsSignature ? mountedWorkspaceIdsSignature.split(',') : []),
    [mountedWorkspaceIdsSignature]
  );
  const activeWorkspaceId = useKernelStore((state) => {
    const projectWorkspaceIds = Array.isArray(state.inventory.Project.registry[projectId]?.workspaceIds)
      ? state.inventory.Project.registry[projectId]?.workspaceIds?.filter(
          (workspaceId): workspaceId is string =>
            typeof workspaceId === 'string' && workspaceId.trim().length > 0
        ) ?? []
      : [];
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const runtimeWorkspaceId = typeof runtime?.UIWorkspaceId === 'string'
      ? runtime.UIWorkspaceId.trim()
      : '';
    if (runtimeWorkspaceId && projectWorkspaceIds.includes(runtimeWorkspaceId)) {
      return runtimeWorkspaceId;
    }
    if (projectWorkspaceIds.length === 0) return '';
    if (projectWorkspaceIds.includes('1')) return '1';
    return projectWorkspaceIds[0] ?? '';
  });
  const isResizingCanvas = useKernelStore((state) => {
    const workspace = state.inventory.StudioWorkspace.registry[activeWorkspaceId];
    return workspace?.isResizingCanvas === true;
  });
  
  const isNativeLayerCutActive = useKernelStore((state) => state.nativeLayerCutSession !== null);
  const currentUserAvatarUrl = dbUserId ? `/api/user/avatar?userId=${dbUserId}` : undefined;
  const [ideogramInpaintPrompt, setIdeogramInpaintPrompt] = useState<IdeogramInpaintPromptState | null>(null);
  const isScissorsWaitingForTarget = activeTool === 'scissors' && !isNativeLayerCutActive;

  useEffect(() => {
    if (!kernel.current || !projectId) return;
    void kernel.current.syncEntity('Project', projectId);
  }, [kernel, projectId]);
  
  useEffect(() => {
    if (!kernel.current || !isNativeLayerCutActive) {
      return;
    }
    if (activeTool !== 'scissors') {
      kernel.current.cancelNativeLayerCut();
    }
  }, [kernel, isNativeLayerCutActive, activeTool]);

  useEffect(() => {
    if (!kernel.current) return;
    if (!isScissorsWaitingForTarget) {
      kernel.current.setScissorsHoverLayerId(null);
    }
  }, [kernel, isScissorsWaitingForTarget]);

  const t = useMemo(() => translations[lang], [lang]);
  const resizeStart = useRef<{ x: number, y: number, initialScale: number, initialRect?: Rect } | null>(null);
  const mouseDownPos = useRef({ x: 0, y: 0 });
  const dragOperationStateRef = useRef<{
    type: 'move' | 'resize';
    initialLayers: Layer[];
    reclick?: LayerReclickPress;
  } | null>(null);
  const gridGenerationPanelState = useKernelStore((state) =>
    state.inventory.Project.registry[projectId]?.runtime?.gridGenerationPanelState ?? null
  );
  const isColumnPickOpen = useKernelStore((state) => state.workspaceColumnPick?.projectId === projectId);
  const isRecastZonePickOpen = useKernelStore((state) => state.recastZonePick?.projectId === projectId);
  useEffect(() => {
    // Opening another project leaves a pick of the one left; a cleanup would also fire on StrictMode's remount.
    const pick = kernel.current?.getWorkspaceColumnPick();
    if (pick && pick.projectId !== projectId) kernel.current?.cancelWorkspaceColumnPick();
    const zonePick = kernel.current?.getRecastZonePick();
    if (zonePick && zonePick.projectId !== projectId) kernel.current?.cancelRecastZonePick();
  }, [kernel, projectId]);
  const annotationCanvasRefs = useRef<Record<string, AnnotationCanvasHandle | null>>({});
  const activeAnnotationWorkspaceIdRef = useRef<string | null>(null);

  const getWorkspaceStack = useCallback((): Workspace[] => {
    if (!kernel.current) return [];
    if (workspaceIds.length > 0) {
      return workspaceIds
        .map((workspaceId) => kernel.current?.getWorkspace(workspaceId))
        .filter((workspace): workspace is Workspace => workspace !== null);
    }
    return [];
  }, [kernel, workspaceIds]);

  const registerAnnotationCanvasRef = useCallback((workspaceId: string, instance: AnnotationCanvasHandle | null) => {
    if (instance) {
      annotationCanvasRefs.current[workspaceId] = instance;
      return;
    }
    delete annotationCanvasRefs.current[workspaceId];
  }, []);

  const openGridGenerationPanel = useCallback((workspaceId: string, payload: GridGenerationPayload) => {
    kernel.current?.patchProjectRuntimeState(projectId, {
      gridGenerationPanelState: {
        workspaceId,
        payload,
        openedAt: Date.now(),
      },
    });
    kernel.current?.focusGenerationAreaForGridPanel(projectId, workspaceId, {
      panelWidthPx: 320,
      panelGapPx: 12,
      viewportPaddingPx: 12,
      bottomOverlayPx: 60,
      marginFactor: 1.03,
    });
  }, [kernel, projectId]);

  const closeGridGenerationPanel = useCallback(() => {
    kernel.current?.patchProjectRuntimeState(projectId, { gridGenerationPanelState: null });
  }, [kernel, projectId]);

  const clearInpaintMaskWithUndo = useCallback((workspaceId: string, source: string) => {
    const kernelHandle = kernel.current;
    if (!kernelHandle) return;
    const previousShapes = cloneMaskShapesForUndo(kernelHandle.getInpaintMaskShapes()[workspaceId]);
    if (previousShapes.length === 0) return;
    kernelHandle.logUserAction('Inpaint Masks Cleared via ContextMenu', { workspaceId, source });
    kernelHandle.executeAction({
      description: 'Clear inpaint mask',
      undo: () => {
        kernel.current?.setInpaintMaskShapes((prev) => ({
          ...prev,
          [workspaceId]: cloneMaskShapesForUndo(previousShapes),
        }));
      },
      redo: () => {
        kernel.current?.setInpaintMaskShapes((prev) => ({ ...prev, [workspaceId]: [] }));
      },
    });
  }, [kernel]);

  const openIdeogramInpaintPrompt = useCallback((
    workspaceId: string,
    clientX: number,
    clientY: number,
    maskPoint: MaskPoint
  ) => {
    const containerRect = kernel.current?.canvasContainerRef.current?.getBoundingClientRect();
    const canvasX = containerRect ? clientX - containerRect.left : clientX;
    const canvasY = containerRect ? clientY - containerRect.top : clientY;
    setIdeogramInpaintPrompt({
      workspaceId,
      canvasX,
      canvasY,
      maskPoint,
      prompt: '',
      openedAt: Date.now(),
    });
    kernel.current?.logUserAction('Ideogram Inpaint Prompt Opened via Canvas Shortcut', {
      workspaceId,
    });
  }, [kernel]);

  const changeIdeogramInpaintPrompt = useCallback((prompt: string) => {
    setIdeogramInpaintPrompt((prev) => prev ? { ...prev, prompt } : prev);
  }, []);

  const cancelIdeogramInpaintPrompt = useCallback(() => {
    setIdeogramInpaintPrompt(null);
  }, []);

  const submitIdeogramInpaintPrompt = useCallback(async () => {
    const promptState = ideogramInpaintPrompt;
    const kernelHandle = kernel.current;
    if (!promptState || !kernelHandle) return;

    const prompt = promptState.prompt.trim();
    if (!prompt) return;

    setIdeogramInpaintPrompt(null);
    await kernelHandle.runIdeogramInpaintOnMask(promptState.workspaceId, prompt, promptState.maskPoint);
  }, [ideogramInpaintPrompt, kernel]);

  const findTopWorkspaceAtWorldPosition = useCallback((
    worldPos: { x: number; y: number },
    options?: { annotationsOnly?: boolean }
  ): Workspace | null => {
    const workspaceSnapshot = getWorkspaceStack();
    for (let i = workspaceSnapshot.length - 1; i >= 0; i--) {
      const workspace = workspaceSnapshot[i];
      if (!workspace || workspace.visible === false) continue;
      if (options?.annotationsOnly && workspace.annotationsEnabled === false) continue;
      const frame = workspace.frameRect;
      if (
        worldPos.x >= frame.x &&
        worldPos.x <= frame.x + frame.w &&
        worldPos.y >= frame.y &&
        worldPos.y <= frame.y + frame.h
      ) {
        return workspace;
      }
    }
    return null;
  }, [getWorkspaceStack]);

  const getWorkspaceOwnedLayers = useCallback((workspaceId: string): Layer[] => {
    if (!kernel.current) return [];
    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return [];

    if ((workspace.layerIds?.length ?? 0) > 0) {
      return workspace.layerIds
        .map((layerId) => kernel.current?.getLayerRegistry()[layerId])
        .filter((layer): layer is Layer => Boolean(layer));
    }

    return kernel.current.getLayersInWorkspaceContext(workspaceId);
  }, [kernel]);

  const onDragStart = useCallback((type: 'move' | 'resize', layerIds: string[], reclick?: LayerReclickPress) => {
    if (!kernel.current) return;
    const relevantLayers = layerIds
      .map((layerId) => kernel.current?.getLayerRegistry()[layerId])
      .filter((layer): layer is Layer => Boolean(layer));
    const initialLayersState = relevantLayers
      .map(cloneLayerSnapshot);

    if (initialLayersState.length > 0) {
      dragOperationStateRef.current = { type, initialLayers: initialLayersState, reclick };
    }
  }, [kernel]);

  useEffect(() => {
    const canvasContainer = kernel.current?.canvasContainerRef.current;
    if (!canvasContainer) return;

    // The scene is anchored to the container's left edge, so a panel opening on the left would drag it along;
    // counter-panning keeps it still on screen, the way the right panel already just covers it.
    // Mounted hidden, under its project's 3D scene, it has no left edge yet: the first one it gets on screen is the anchor.
    const mountRect = canvasContainer.getBoundingClientRect();
    let lastLeft = mountRect.width === 0 ? null : mountRect.left;
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setViewportSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
      const rect = canvasContainer.getBoundingClientRect();
      if (rect.width === 0) return; // Hidden: its left edge reads 0 and would fling the scene.
      if (lastLeft !== null && rect.left !== lastLeft) kernel.current?.shiftViewportPan(projectId, lastLeft - rect.left, 0);
      lastLeft = rect.left;
      const containerBox = entries[entries.length - 1]?.contentRect;
      if (containerBox) starfieldRef.current?.resize(containerBox.width, containerBox.height);
    });

    resizeObserver.observe(canvasContainer);
    return () => resizeObserver.disconnect();
  }, [kernel, projectId]);

  const [draggedWorkspaceId, setDraggedWorkspaceId] = useState<string | null>(null);

  const canvasControlDragStart = useRef<{x: number, y: number} | null>(null);
  const mouseDownTimeRef = useRef<number>(0);
  const panStartScreenRef = useRef<{ x: number; y: number } | null>(null);
  const panStartOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const transientPanOffsetRef = useRef<{ x: number; y: number } | null>(null);

  const startViewportPan = useCallback((clientX: number, clientY: number) => {
    if (!kernel.current) return;
    const basePan = transientPanOffsetRef.current ?? getProjectPanOffset(kernel.current, projectId);
    panStartScreenRef.current = { x: clientX, y: clientY };
    panStartOffsetRef.current = { x: basePan.x, y: basePan.y };
    transientPanOffsetRef.current = { x: basePan.x, y: basePan.y };
    setProjectIsPanning(kernel.current, projectId, true);
  }, [kernel, projectId]);

  const screenToWorld = useCallback((screenX: number, screenY: number): { x: number, y: number } => {
    if (!kernel.current?.canvasContainerRef.current) return { x: 0, y: 0 };
    let offset = getProjectPanOffset(kernel.current, projectId);
    let zoom = getProjectZoomLevel(kernel.current, projectId);
    const rect = kernel.current.canvasContainerRef.current.getBoundingClientRect();
    const worldX = (screenX - rect.left - offset.x) / zoom;
    const worldY = (screenY - rect.top - offset.y) / zoom;
    return { x: worldX, y: worldY };
  }, [kernel, projectId]);

  const {
    shapeDrawingDraft,
    pencilDrawingDraft,
    polygonDrawingDraft,
    textDrawingDraft,
    drawingStyle,
    drawingPanelRequestToken,
    handleDrawingPointerDown,
    handleDrawingPointerMove,
    handleDrawingPointerUp,
    handleDrawingDoubleClick,
    handleTextDrawingBlur,
    handleTextDraftChange,
    cancelTextDrawing,
    commitTextDrawingDraft,
    startEditingDrawingTextLayer,
    rerenderTextDrawingLayerAtCurrentSize,
  } = useCanvasDrawingToolController({
    kernel,
    projectId,
    screenToWorld,
    findTopWorkspaceAtWorldPosition,
  });

  const findTopLayerAtWorldPosition = useCallback((workspace: Workspace, worldPos: { x: number; y: number }): Layer | null => {
    if (!kernel.current) return null;

    const layersInContext = kernel.current.getLayersInWorkspaceContext(workspace.id);

    // Iterate from top to bottom (reverse order of rendering)
    for (let i = layersInContext.length - 1; i >= 0; i--) {
      const layer = layersInContext[i];

      if (!layer || !layer.visible) {
        continue;
      }

      // A layer's world position is its rect relative to the CURRENT rendering context's frame,
      // not its own owner's frame. This is crucial for parent-child workspace rendering.
      const layerWorldX = workspace.frameRect.x + layer.rect.x;
      const layerWorldY = workspace.frameRect.y + layer.rect.y;

      const scale = Math.max(layer.scale ?? 1, 0.0001);
      const layerWorldRect = {
        x: layerWorldX,
        y: layerWorldY,
        w: layer.imageData ? layer.imageData.width * scale : layer.rect.w,
        h: layer.imageData ? layer.imageData.height * scale : layer.rect.h,
      };
      const transform = resolveStudioLayerTransform(layer.params);
      const localPoint = projectStudioLayerPointToLocal(worldPos, layerWorldRect, transform);

      if (
        localPoint.x >= 0 && localPoint.x <= layerWorldRect.w &&
        localPoint.y >= 0 && localPoint.y <= layerWorldRect.h
      ) {
        if (layer.source === 'drawing') {
          return layer;
        }

        if (layer.imageData && layer.type !== 'video') {
          const pixelX = Math.floor(localPoint.x / scale);
          const pixelY = Math.floor(localPoint.y / scale);

          if (pixelX >= 0 && pixelX < layer.imageData.width && pixelY >= 0 && pixelY < layer.imageData.height) {
            const alphaIndex = (pixelY * layer.imageData.width + pixelX) * 4 + 3;
            if ((layer.imageData.data[alphaIndex] ?? 0) > 10) { // Small threshold for alpha to count as a hit.
              return layer;
            }
          }
          // If transparent, continue loop to check layers below.
        } else {
          // For non-image layers or layers without pixel data, bounding box hit is enough.
          return layer;
        }
      }
    }

    return null;
  }, [kernel]);

  // The canvas's own selection path: a layer click reaches the container (pan), and this fast-click hit test selects;
  // WorkspaceCard's layer mousedown was not reached in the default tool (2026-10-16).
  const selectLayerAtWorldPosition = useCallback(
    (workspace: Workspace, worldPos: { x: number; y: number }, isShiftClick: boolean) => {
      if (!kernel.current) return null;

      const clickedLayer = findTopLayerAtWorldPosition(workspace, worldPos);
      if (!clickedLayer) return null;

      const wasSelected = getProjectSelectedLayersId(kernel.current, projectId).includes(clickedLayer.id);
      kernel.current.selectLayer(projectId, clickedLayer.id, isShiftClick);
      if (wasSelected && !isShiftClick) {
        requestProjectLayerListReveal(kernel.current, projectId, clickedLayer.id);
        kernel.current.bumpLayerPulseToken(clickedLayer.id);
      }

      return clickedLayer;
    },
    [findTopLayerAtWorldPosition, kernel, projectId]
  );

  const findTopCuttableLayerAtWorldPosition = useCallback((worldPos: { x: number; y: number }) => {
    if (!kernel.current) return null;
    const workspaceSnapshot = getWorkspaceStack();

    for (let i = workspaceSnapshot.length - 1; i >= 0; i--) {
      const ws = workspaceSnapshot[i];
      if (!ws || !(ws.visible ?? true)) continue;

      const frame = ws.frameRect;
      if (
        worldPos.x < frame.x || worldPos.x > frame.x + frame.w ||
        worldPos.y < frame.y || worldPos.y > frame.y + frame.h
      ) {
        continue;
      }

      const hitLayer = findTopLayerAtWorldPosition(ws, worldPos);
      if (!hitLayer) continue;
      if (!(ws.layerIds ?? []).includes(hitLayer.id)) continue;
      if (hitLayer.type === 'video' || hitLayer.type === 'audio') continue;
      return { workspace: ws, layer: hitLayer };
    }

    return null;
  }, [findTopLayerAtWorldPosition, getWorkspaceStack, kernel]);

  const handleWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const { deltaY, deltaX, ctrlKey, metaKey } = event;
    const canvasContainer = kernel.current?.canvasContainerRef?.current;
    if (!kernel.current || !canvasContainer) return;
    const currentActiveTool = kernel.current.getActiveTool();

    if (
      event.altKey &&
      (currentActiveTool === 'annotationLine' || currentActiveTool === 'annotationEraser')
    ) {
      event.preventDefault();
      const currentSize = kernel.current.getAnnotationBrushSize?.() ?? 1;
      const delta = -deltaY * 0.05;
      const nextSize = Math.max(1, Math.round(currentSize + delta));
      if (nextSize !== currentSize) {
        kernel.current.setAnnotationBrushSize(nextSize);
        kernel.current.logUserAction('Annotation Tool Size Changed', {
          tool: currentActiveTool,
          new_size: nextSize,
          previous_size: currentSize,
          source: 'alt+scroll',
        });
      }
      return;
    }
    event.preventDefault();
    if (ctrlKey || metaKey) { // Zooming attempt
      if (Math.abs(deltaY) > 25) {
        kernel.current?.handleZoom(projectId, event, canvasContainer);
      } else {
        const rect = canvasContainer.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        const microZoomSensitivity = 0.05; // Tune this!
        const zoomMultiplier = Math.exp(-deltaY * microZoomSensitivity);

        const currentZoomVal = getProjectZoomLevel(kernel.current, projectId); // from props
        const currentPan = getProjectPanOffset(kernel.current, projectId);   // from props

        const newInstantZoom = Math.min(4096.0, Math.max(0.04, currentZoomVal * zoomMultiplier));

        const newInstantPan = kernel.current.clampViewportPanForProject(projectId, {
          x: mouseX - (mouseX - currentPan.x) * (newInstantZoom / currentZoomVal),
          y: mouseY - (mouseY - currentPan.y) * (newInstantZoom / currentZoomVal),
        }, newInstantZoom);
        kernel.current?.applyViewportTransform(newInstantPan, newInstantZoom);
        setProjectZoomLevel(kernel.current, projectId, newInstantZoom);
        setProjectPanOffset(kernel.current, projectId, newInstantPan);
      }
    } else {
      kernel.current.handleWheelPan(projectId, deltaX, deltaY);
    }
  }, [
    kernel, projectId
  ]);



  const handleCanvasContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!kernel.current) {
      return;
    }
    const { clientX, clientY } = event;
    // Mask Pen: right-click opens a small menu (toggle pen/brush + delete all masks).
    if (kernel.current.getActiveTool() === 'maskPen') {
      const maskPenWorkspaceId = kernel.current.getProjectUIWorkspaceId(projectId);
      const currentMode = kernel.current.getMaskPenMode();
      const maskPenWorkspace = maskPenWorkspaceId ? kernel.current.getWorkspace(maskPenWorkspaceId) : null;
      const maskShapeCount = maskPenWorkspaceId
        ? (kernel.current.getInpaintMaskShapes()[maskPenWorkspaceId]?.length ?? 0)
        : 0;
      const maskPenShapes = maskPenWorkspaceId
        ? (kernel.current.getInpaintMaskShapes()[maskPenWorkspaceId] ?? [])
        : [];
      const maskPenMaskPoint = maskPenWorkspace
        ? worldPointToWorkspaceRasterPoint(screenToWorld(clientX, clientY), maskPenWorkspace.frameRect)
        : null;
      const canOpenMaskPenInpaintPrompt =
        !!maskPenWorkspaceId &&
        !!maskPenMaskPoint &&
        inpaintMaskHasContent(maskPenShapes) &&
        isPointInsideInpaintMask(maskPenMaskPoint, maskPenShapes);
      kernel.current.setMenuData({
        position: { x: event.clientX, y: event.clientY },
        actions: ([
          canOpenMaskPenInpaintPrompt ? {
            label: t.inpaint,
            onClick: () => {
              if (!maskPenWorkspaceId || !maskPenMaskPoint) return;
              openIdeogramInpaintPrompt(maskPenWorkspaceId, clientX, clientY, maskPenMaskPoint);
            },
          } : null,
          {
            label: currentMode === 'pen' ? 'Switch to brush' : 'Switch to pen',
            onClick: () => {
              kernel.current?.setMaskPenMode(currentMode === 'pen' ? 'brush' : 'pen');
              kernel.current?.setMenuData(null);
            },
          },
          {
            label: 'Delete all masks',
            disabled: maskShapeCount === 0,
            onClick: () => {
              if (maskPenWorkspaceId) {
                clearInpaintMaskWithUndo(maskPenWorkspaceId, 'maskPen');
              }
              kernel.current?.setMenuData(null);
            },
          },
        ] as (QuickMenuAction | null)[]).filter((action): action is QuickMenuAction => action !== null),
      });
      return;
    }
    if (isNativeLayerCutActive) {
      kernel.current.setMenuData(null);
      return;
    }

    const worldPos = screenToWorld(clientX, clientY);
    const workspaceSnapshot = getWorkspaceStack();
    const activeWorkspaceId = kernel.current.getProjectUIWorkspaceId(projectId);

    // 1. Check for workspace hit (in reverse order to hit topmost first)
    for (let i = workspaceSnapshot.length - 1; i >= 0; i--) {
      const ws = workspaceSnapshot[i];
      if(!ws) continue;
      if (!(ws.visible ?? true)) continue;

      const frame = ws.frameRect;
      if (worldPos.x >= frame.x && worldPos.x <= frame.x + frame.w &&
          worldPos.y >= frame.y && worldPos.y <= frame.y + frame.h) {
        
        if (ws.id === activeWorkspaceId) {
          const targetWorkspaceId = ws.id;
          selectLayerAtWorldPosition(ws, worldPos, event.shiftKey);

          // --- ACTIVE WORKSPACE MENU (MODIFIED logic) ---
          const layersInContext = kernel.current.getLayersInWorkspaceContext(ws.id);
          const hasVisibleLayers = layersInContext.some(l => l.visible);
          const hasVisibleTimelineMedia = layersInContext.some((layer) => (
            layer.visible !== false &&
            (layer.type === 'video' || layer.type === 'audio')
          ));
          const hasVectorizableVisibleLayers = layersInContext.some((layer) => (
            layer.visible !== false &&
            !isLayerVectorized(layer) &&
            layer.isVectorizing !== true &&
            layer.type !== 'video' &&
            layer.type !== 'audio'
          ));
          const selectedLayerId = getProjectSelectedLayersId(kernel.current, projectId)[0];
          const selectedLayer = selectedLayerId !== undefined
            ? (kernel.current.getLayerRegistry()[selectedLayerId]) ?? null
            : null;
          const selectedCuttableLayer =
            selectedLayer &&
            (ws.layerIds ?? []).includes(selectedLayer.id) &&
            selectedLayer.type !== 'video' &&
            selectedLayer.type !== 'svg'
              ? selectedLayer
              : null;

          const genRect = ws.generationRect;
          const clickedOutsideGenerationArea =
            worldPos.x < genRect.x ||
            worldPos.x > genRect.x + genRect.w ||
            worldPos.y < genRect.y ||
            worldPos.y > genRect.y + genRect.h;
          const targetInpaintMaskShapes = kernel.current.getInpaintMaskShapes()[targetWorkspaceId] ?? [];
          const targetMaskPoint = worldPointToWorkspaceRasterPoint(worldPos, ws.frameRect);
          const canOpenIdeogramInpaintPrompt =
            !!targetMaskPoint &&
            inpaintMaskHasContent(targetInpaintMaskShapes) &&
            isPointInsideInpaintMask(targetMaskPoint, targetInpaintMaskShapes);

          const sweepUpscaleAction = (gridSize: SweepUpscaleGridSize) => {
            const label = gridSize === 2 ? t.upscale8k : t.upscale16k;
            const credits = quoteSweepUpscaleCredits(gridSize);
            return {
              label,
              tooltip: (gridSize === 2 ? t.upscale8kTooltip : t.upscale16kTooltip)(credits),
              customContent: credits === null ? undefined : (
                <span className="flex items-center gap-2">
                  {label}
                  <CreditsBadge value={String(credits)} size="sm" />
                </span>
              ),
              onClick: () => {
                if (!kernel.current) return;

                kernel.current.logUserAction('Sweep Upscale Triggered', {
                  workspaceId: targetWorkspaceId,
                  gridSize,
                  source: 'inpaint_powers_sweep_upscale',
                });
                kernel.current.sweepUpscale(targetWorkspaceId, gridSize).catch(error => {
                  console.error(`${label} failed`, error);
                  kernel.current?.handleError(error?.message || `${label} failed.`);
                });
                kernel.current.setMenuData(null);
              }
            };
          };

          const streetDevelopAction = (recipe: (typeof STREET_DEVELOP_RECIPES)[number]): QuickMenuAction => ({
            label: recipe.label,
            tooltip: t.develop360Tooltip,
            onClick: () => {
              kernel.current?.setMenuData(null);
              kernel.current?.openWorkspaceColumnPick(targetWorkspaceId, { purpose: 'develop', recipeId: recipe.id });
            }
          });

          const streetSeamAction: QuickMenuAction = {
            label: t.setSeam,
            tooltip: t.setSeamTooltip,
            onClick: () => {
              kernel.current?.setMenuData(null);
              kernel.current?.openWorkspaceColumnPick(targetWorkspaceId, { purpose: 'seam' });
            }
          };

          const repairCutsErrors = streetRepairErrors(rememberedStreetCuts(layersInContext), ws.frameRect);
          const streetTextureAction = (recipe: (typeof STREET_TEXTURE_RECIPES)[number]): QuickMenuAction => ({
            label: recipe.label,
            tooltip: t.texturiseTooltip,
            onClick: () => {
              if (!kernel.current) return;
              kernel.current.setMenuData(null);
              kernel.current.logUserAction('Street Texturise Triggered', { workspaceId: targetWorkspaceId, recipeId: recipe.id });
              kernel.current.texturiseStreet(targetWorkspaceId, recipe.id).catch((error) => {
                kernel.current?.handleError(error instanceof Error ? error.message : `${recipe.label} failed.`);
              });
            }
          });

          const streetRepairAction: QuickMenuAction = {
            label: STREET_REPAIR_RECIPE.label,
            tooltip: repairCutsErrors.length ? repairCutsErrors.join(' ') : t.repairCutsTooltip,
            disabled: repairCutsErrors.length > 0,
            onClick: () => {
              if (!kernel.current) return;
              kernel.current.setMenuData(null);
              kernel.current.logUserAction('Street Repair Cuts Triggered', { workspaceId: targetWorkspaceId });
              kernel.current.repairStreetCuts(targetWorkspaceId).catch((error) => {
                kernel.current?.handleError(error instanceof Error ? error.message : `${STREET_REPAIR_RECIPE.label} failed.`);
              });
            }
          };

          const recastCredits = quoteRecipeCredits(RECAST_EXTRAS_RECIPE.model, RECAST_EXTRAS_RECIPE.params, RECAST_EXTRAS_RECIPE.prompt, 1);
          const withRecastCredits = (label: string, value: string | null) => (value === null ? undefined : (
            <span className="flex items-center gap-2">
              {label}
              <CreditsBadge value={value} size="sm" />
            </span>
          ));

          const recastExtrasAction: QuickMenuAction = kernel.current.isRecastExtrasRunning(targetWorkspaceId) ? {
            label: t.stopRecastExtras,
            tooltip: t.stopRecastExtrasTooltip,
            onClick: () => {
              kernel.current?.setMenuData(null);
              kernel.current?.logUserAction('Recast Extras Stopped', { workspaceId: targetWorkspaceId });
              kernel.current?.stopRecastExtras(targetWorkspaceId);
            }
          } : {
            label: t.recastExtras,
            tooltip: t.recastExtrasTooltip,
            customContent: withRecastCredits(t.recastExtras, recastCredits === null ? null : `${recastCredits}/zone`),
            onClick: () => {
              kernel.current?.setMenuData(null);
              void kernel.current?.openRecastZonePick(targetWorkspaceId);
            }
          };

          const maskMenu: QuickMenuAction = {
            label: t.clearAnnotations,
            onClick: () => {
              kernel.current?.logUserAction('Annotations Cleared via ContextMenu');
              kernel.current?.clearWorkspaceAnnotations(targetWorkspaceId);
            }
          };
          const currentActions = ([
            {
              label: t.layerMenu,
              onClick: () => {},
              subActions: [
                {
                  label: t.cut,
                  disabled: !selectedCuttableLayer,
                  onClick: () => {
                    if (!kernel.current || !selectedCuttableLayer) return;

                    kernel.current.logUserAction('Layer Cut Triggered via Canvas ContextMenu', {
                      layer_id: selectedCuttableLayer.id,
                      layer_name: selectedCuttableLayer.name,
                      workspaceId: targetWorkspaceId,
                    });
                    kernel.current.cutLayer(selectedCuttableLayer).catch(err => {
                      kernel.current?.handleError(err?.message || 'Layer Cut failed.');
                    });
                    kernel.current.setMenuData(null);
                  }
                },
                {
                  label: t.integrate,
                  disabled: !selectedCuttableLayer,
                  onClick: () => {
                    if (!kernel.current || !selectedCuttableLayer) return;

                    kernel.current.logUserAction('Layer Integrate Triggered', {
                      layer_id: selectedCuttableLayer.id,
                      layer_name: selectedCuttableLayer.name,
                      workspaceId: targetWorkspaceId,
                    });
                    kernel.current.integrateLayer(selectedCuttableLayer).catch(err => {
                      console.error(err);
                      kernel.current?.handleError(err?.message || 'Integrate failed.');
                    });
                    kernel.current.setMenuData(null);
                  }
                },
                {
                  label: t.harmonize,
                  disabled: !selectedCuttableLayer,
                  onClick: () => {
                    if (!kernel.current || !selectedCuttableLayer) return;

                    kernel.current.logUserAction('Layer Harmonize Unchanged Triggered', {
                      layer_id: selectedCuttableLayer.id,
                      layer_name: selectedCuttableLayer.name,
                      workspaceId: targetWorkspaceId,
                    });
                    kernel.current.harmonizeLayerUnchanged(selectedCuttableLayer).catch(err => {
                      console.error(err);
                      kernel.current?.handleError(err?.message || 'Harmonize failed.');
                    });
                    kernel.current.setMenuData(null);
                  }
                },
                {
                  label: t.blend,
                  disabled: !selectedCuttableLayer,
                  onClick: () => {
                    if (!kernel.current || !selectedCuttableLayer) return;

                    kernel.current.logUserAction('Layer Blend Triggered', {
                      layer_id: selectedCuttableLayer.id,
                      layer_name: selectedCuttableLayer.name,
                      workspaceId: targetWorkspaceId,
                    });
                    kernel.current.blendLayer(selectedCuttableLayer, 16).catch(err => {
                      console.error(err);
                      kernel.current?.handleError(err?.message || 'Feather Edges failed.');
                    });
                    kernel.current.setMenuData(null);
                  }
                },
                {
                  label: t.renderCanvasToLayer,
                  onClick: () => {
                    void kernel.current?.renderWorkspaceAreaToNewLayer(projectId, ws.id);
                  }
                },
              ]
            },
            hasVisibleLayers ? {
              label: t.filter,
              onClick: () => {
                if (!kernel.current) return;
                kernel.current.patchProjectRuntimeState(projectId, {
                  developSession: {
                    workspaceId: targetWorkspaceId,
                    settings: DEFAULT_STUDIO_DEVELOP_SETTINGS,
                    openedAt: Date.now(),
                  },
                });
                setProjectRightSidebarTab(kernel.current, projectId, 'models');
                kernel.current.openRightPanel(360);
                kernel.current.logUserAction('Workspace Filter Panel Opened', {
                  projectId,
                  workspaceId: targetWorkspaceId,
                });
                kernel.current.setMenuData(null);
              }
            } : null,
            hasVisibleLayers ? {
              label: t.powers,
                onClick: () => {}, // Parent item, no-op
                subActions: [
                  {
                    label: t.quickUpscale,
                    tooltip: t.quickUpscaleTooltip,
                    onClick: () => {
                      if (!kernel.current) return;
  
                      kernel.current.logUserAction('Quick Upscale Triggered', { workspaceId: targetWorkspaceId });
                      kernel.current.quickUpscale(targetWorkspaceId).catch(error => {
                        console.error('Quick Upscale failed', error);
                      });
                      kernel.current.setMenuData(null);
                    },
                    onContextMenu: () => {
                      if (!kernel.current) return;
                      openGridGenerationPanel(targetWorkspaceId, {
                        modelPreset: 'upscale',
                        modelId: 'fater-upscale',
                        source: 'inpaint_powers_quick_upscale_context_menu',
                      });
                      kernel.current.setMenuData(null);
                    }
                  },
                  {
                    label: t.quickEnhance,
                    tooltip: t.quickEnhanceTooltip,
                    onClick: () => {
                      if (!kernel.current) return;
  
                      kernel.current.logUserAction('Quick Enhance Triggered', { workspaceId: targetWorkspaceId });
                      kernel.current.quickEnhance(targetWorkspaceId).catch(error => {
                        console.error('Quick Enhance failed', error);
                      });
                      kernel.current.setMenuData(null);
                    },
                    onContextMenu: () => {
                      if (!kernel.current) return;
                      openGridGenerationPanel(targetWorkspaceId, {
                        modelPreset: 'enhance',
                        modelId: 'fater-enhance',
                        source: 'inpaint_powers_quick_enhance_context_menu',
                      });
                      kernel.current.setMenuData(null);
                    }
                  },
                  sweepUpscaleAction(2),
                  sweepUpscaleAction(3),
                  {
                    label: t.segment,
                    tooltip: t.segmentTooltip,
                    onClick: () => {
                      if (!kernel.current) return;

                      kernel.current.logUserAction('Quick Segment Triggered', { workspaceId: targetWorkspaceId });
                      kernel.current.autoCut(targetWorkspaceId).catch(error => {
                        console.error('Quick Segment failed', error);
                      });
                      kernel.current.setMenuData(null);
                    }
                  },
                  {
                    label: t.vectorizeVisibleLayers,
                    tooltip: t.vectorizeVisibleLayersTooltip,
                    disabled: !hasVectorizableVisibleLayers,
                    onClick: () => {
                      if (!hasVectorizableVisibleLayers) return;
                      if (!kernel.current) return;
                      kernel.current.logUserAction('Workspace Vectorize Visible Layers Triggered', {
                        workspaceId: targetWorkspaceId,
                      });
                      kernel.current.vectorizeVisibleLayers(targetWorkspaceId).catch((error) => {
                        console.error(error);
                        kernel.current?.handleError(error?.message || 'Workspace vectorisation failed.');
                      });
                      kernel.current.setMenuData(null);
                    }
                  },
                  recastExtrasAction,
                  {
                    label: t.recastGenerationArea,
                    tooltip: t.recastGenerationAreaTooltip,
                    customContent: withRecastCredits(t.recastGenerationArea, recastCredits === null ? null : String(recastCredits)),
                    disabled: kernel.current.isRecastExtrasRunning(targetWorkspaceId),
                    onClick: () => {
                      kernel.current?.setMenuData(null);
                      kernel.current?.logUserAction('Recast Generation Area Triggered', { workspaceId: targetWorkspaceId });
                      kernel.current?.recastGenerationArea(targetWorkspaceId).catch((error) => {
                        console.error(error);
                        kernel.current?.handleError(error?.message || 'Recast extras failed.');
                      });
                    }
                  },
                  // Only a project with a 3D scene, for the user it is served to.
                  ...(projectSurfaceOf(kernel.current.getState(), projectId) ? [{
                    label: t.rebuildIn3d,
                    tooltip: t.rebuildIn3dTooltip,
                    onClick: () => {
                      if (!kernel.current) return;
                      kernel.current.logUserAction('Workspace Rebuild In 3D Triggered', { projectId, workspaceId: targetWorkspaceId });
                      prepareRebuildMessage(kernel.current, projectId, targetWorkspaceId).catch((error) => {
                        console.error('Rebuild in 3D failed', error);
                        kernel.current?.handleError('The workspace could not be sent to the 3D scene.');
                      });
                      kernel.current.setMenuData(null);
                    }
                  }] : []),
                ]
              } : null,
            hasVisibleLayers && isStreetPanoramaFrame(ws.frameRect) ? {
              label: t.develop360,
              onClick: () => {},
              subActions: [streetSeamAction, ...STREET_TEXTURE_RECIPES.map(streetTextureAction), ...STREET_DEVELOP_RECIPES.map(streetDevelopAction), streetRepairAction],
            } : null,
            canOpenIdeogramInpaintPrompt ? {
              label: t.inpaint,
              onClick: () => {
                if (!targetMaskPoint) return;
                openIdeogramInpaintPrompt(targetWorkspaceId, clientX, clientY, targetMaskPoint);
              },
            } : null,
            maskMenu,
            inpaintMaskHasContent(targetInpaintMaskShapes) ? {
              label: kernel.current.getMasksHidden?.() ? 'Show masks' : 'Hide masks',
              onClick: () => {
                kernel.current?.logUserAction('Masks Visibility Toggled via ContextMenu', { workspaceId: targetWorkspaceId, hidden: !kernel.current?.getMasksHidden?.() });
                kernel.current?.setMasksHidden?.((prev) => !prev);
                kernel.current?.setMenuData(null);
              },
            } : null,
            inpaintMaskHasContent(targetInpaintMaskShapes) ? {
              label: 'Delete all masks',
              onClick: () => {
                clearInpaintMaskWithUndo(targetWorkspaceId, 'workspaceMenu');
                kernel.current?.setMenuData(null);
              },
            } : null,
            hasVisibleLayers ? {
              label: t.download,
              onClick: hasVisibleTimelineMedia ? () => {} : () => {
                const target = clickedOutsideGenerationArea ? 'frame' : 'area';
                kernel.current?.logUserAction('Inpaint Image Export Modal Opened', {
                  workspaceId: targetWorkspaceId,
                  target,
                });
                kernel.current?.openModal('ImageExport', {
                  workspaceId: targetWorkspaceId,
                  defaultTarget: target,
                  exportMode: 'image',
                });
                kernel.current?.setMenuData(null);
              },
              subActions: hasVisibleTimelineMedia ? [
                {
                  label: t.exportImage,
                  onClick: () => {
                    const target = clickedOutsideGenerationArea ? 'frame' : 'area';
                    kernel.current?.logUserAction('Inpaint Image Export Modal Opened', {
                      workspaceId: targetWorkspaceId,
                      target,
                    });
                    kernel.current?.openModal('ImageExport', {
                      workspaceId: targetWorkspaceId,
                      defaultTarget: target,
                      exportMode: 'image',
                    });
                    kernel.current?.setMenuData(null);
                  },
                },
                {
                  label: t.exportVideo,
                  onClick: () => {
                    const target = clickedOutsideGenerationArea ? 'frame' : 'area';
                    kernel.current?.logUserAction('Inpaint Video Export Modal Opened', {
                      workspaceId: targetWorkspaceId,
                      target,
                    });
                    kernel.current?.openModal('ImageExport', {
                      workspaceId: targetWorkspaceId,
                      defaultTarget: target,
                      exportMode: 'video',
                    });
                    kernel.current?.setMenuData(null);
                  },
                },
              ] : undefined,
            } : null,
            {
              label: t.import,
              onClick: () => {
                kernel.current?.promptAndAddLayerFromFile();
                kernel.current?.logUserAction('Layer Imported via ContextMenu');
              }
            },
          ] as (QuickMenuAction | null)[]).filter((action): action is QuickMenuAction => action !== null);
          kernel.current?.setMenuData({ position: { x: clientX, y: clientY }, actions: currentActions });

        } else {
          // --- INACTIVE WORKSPACE MENU ---
          const inactiveWorkspaceActions = ([
            {
              label: t.switchTo(ws.name),
              onClick: () => {
                kernel.current?.setProjectUIWorkspaceId(projectId, ws.id);
                kernel.current?.logUserAction('Workspace Switched by ContextMenu', { target_workspace_id: ws.id });
              }
            },
            {
              label: t.duplicateNamed(ws.name),
              onClick: () => {
                kernel.current?.duplicateWorkspace(projectId, ws.id, [`${ws.name} Copy`]);
              }
            },
            {
              label: t.deleteNamed(ws.name),
              onClick: () => {
                kernel.current?.deleteWorkspace(projectId, ws.id);
              }
            }
          ] as (QuickMenuAction | null)[]).filter((action): action is QuickMenuAction => action !== null);
          kernel.current?.setMenuData({ position: { x: clientX, y: clientY }, actions: inactiveWorkspaceActions });
        }
        return;
      }
    }

    // 2. Clicked in the void. A Vector project is one artboard until artboards ship; nothing to add there.
    if (kernel.current?.getInventory().Project.registry[projectId]?.projectType === 'VECTOR') return;
    const voidActions: QuickMenuData['actions'] = [
      {
        label: t.addWorkspace,
        onClick: () => {
          kernel.current?.handleAddWorkspace(projectId);
        }
      }
    ];
    kernel.current?.setMenuData({ position: { x: clientX, y: clientY }, actions: voidActions });
  }, [
    kernel, screenToWorld, t, selectLayerAtWorldPosition, isNativeLayerCutActive, getWorkspaceStack, projectId, openGridGenerationPanel, clearInpaintMaskWithUndo, openIdeogramInpaintPrompt
  ]);


  const handleContainerMouseDown = useCallback(
    async (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      const isTouch = 'touches' in event;
      const clientX = isTouch ? (event.touches[0]?.clientX || 0) : (event as React.MouseEvent).clientX;
      const clientY = isTouch ? (event.touches[0]?.clientY || 0) : (event as React.MouseEvent).clientY;
      const button = isTouch ? 0 : (event as React.MouseEvent).button;

      kernel.current?.canvasContainerRef.current?.focus({ preventScroll: true });
      mouseDownTimeRef.current = Date.now();
      if (button === 0) {
        mouseDownPos.current = { x: clientX, y: clientY };
      }

      const worldPos = screenToWorld(clientX, clientY);
      const currentToolAtPointerDown = kernel.current?.getActiveTool();
      const lockedAnnotationWorkspaceId = activeAnnotationWorkspaceIdRef.current;
      const lockedAnnotationHandle = lockedAnnotationWorkspaceId
        ? annotationCanvasRefs.current[lockedAnnotationWorkspaceId]
        : null;
      if (
        (isTouch || button === 0) &&
        currentToolAtPointerDown === 'maskPen' &&
        lockedAnnotationWorkspaceId &&
        lockedAnnotationHandle?.hasActiveMaskDraft()
      ) {
        const handledByLockedMask = await lockedAnnotationHandle.handleMouseDown(event);
        if (handledByLockedMask) {
          if (!lockedAnnotationHandle.hasActiveMaskDraft()) {
            activeAnnotationWorkspaceIdRef.current = null;
          }
          return;
        }
      }
      activeAnnotationWorkspaceIdRef.current = null;

      if (!isTouch && (event as React.MouseEvent).button === 1) {
        mouseDownPos.current = { x: clientX, y: clientY };
        startViewportPan(clientX, clientY);
        return;
      }

      if (isScissorsWaitingForTarget && (isTouch || (event as React.MouseEvent).button === 0)) {
        const target = findTopCuttableLayerAtWorldPosition(worldPos);
        if (!kernel.current) return;

        if (!target) {
          kernel.current.setScissorsHoverLayerId(null);
          return;
        }

        kernel.current.setScissorsHoverLayerId(target.layer.id);
        if (target.workspace.id !== kernel.current.getProjectUIWorkspaceId(projectId)) {
          kernel.current.setProjectUIWorkspaceId(projectId, target.workspace.id);
        }

        kernel.current.selectLayer(projectId, target.layer.id, false);
        kernel.current.logUserAction('Layer Cut Target Selected', {
          layer_id: target.layer.id,
          layer_name: target.layer.name,
          workspaceId: target.workspace.id,
        });
        kernel.current.cutLayer(target.layer).catch((err) => {
          kernel.current?.handleError(err?.message || 'Layer Cut failed.');
        });
        return;
      }

      if (kernel.current?.getActiveTool() === 'scissors') {
        return;
      }

      if (isNativeLayerCutActive && (isTouch || (event as React.MouseEvent).button === 0)) {
        mouseDownPos.current = { x: clientX, y: clientY };
        startViewportPan(clientX, clientY);
        return;
      }

      if (handleDrawingPointerDown(event, clientX, clientY, isTouch, button)) {
        return;
      }

      if (isTouch || (event as React.MouseEvent).button === 0) {
        const workspaceSnapshot = getWorkspaceStack();
        for (let i = workspaceSnapshot.length - 1; i >= 0; i--) {
          const ws = workspaceSnapshot[i];
          if(!ws) continue;
          if (!ws.visible) continue;

          const frame = ws.frameRect;
          if (worldPos.x >= frame.x && worldPos.x <= frame.x + frame.w &&
              worldPos.y >= frame.y && worldPos.y <= frame.y + frame.h) {
            
            if (ws.id !== kernel.current?.getProjectUIWorkspaceId(projectId)) {
              kernel.current?.setProjectUIWorkspaceId(projectId, ws.id);
              kernel.current?.logUserAction('Workspace Switched by Click', { target_workspace_id: ws.id, reason: 'frame_hit' });
            }

            if (!ws.locked && currentToolAtPointerDown === null) {
              setDraggedWorkspaceId(ws.id);
              return;
            }
            break;
          }
        }
      }

      const currentTool = kernel.current?.getActiveTool();
      const isAnnotationTool = currentTool === 'annotationLine' || currentTool === 'annotationEraser' || currentTool === 'comment' || currentTool === 'maskPen';
      const canAnnotationCanvasHandlePointer = isAnnotationTool || currentTool === null;

      if ((isTouch || (event as React.MouseEvent).button === 0) && isAnnotationTool) {
        const annotationWorkspace = findTopWorkspaceAtWorldPosition(worldPos, { annotationsOnly: currentTool !== 'maskPen' });
        if (!annotationWorkspace) {
          startViewportPan(clientX, clientY);
          return;
        }
      }

      if ((isTouch || (event as React.MouseEvent).button === 0) && canAnnotationCanvasHandlePointer) {
        const annotationWorkspace = findTopWorkspaceAtWorldPosition(worldPos, { annotationsOnly: currentTool !== 'maskPen' });
        if (annotationWorkspace) {
          if (annotationWorkspace.id !== kernel.current?.getProjectUIWorkspaceId(projectId)) {
            kernel.current?.setProjectUIWorkspaceId(projectId, annotationWorkspace.id);
            kernel.current?.logUserAction('Workspace Switched by Annotation Interaction', {
              target_workspace_id: annotationWorkspace.id,
              reason: 'annotation_hit',
            });
          }

          const annotationHandle = annotationCanvasRefs.current[annotationWorkspace.id];
          const handledByMask = await annotationHandle?.handleMouseDown(event);
          if (handledByMask) {
            activeAnnotationWorkspaceIdRef.current = annotationWorkspace.id;
            return;
          }
        }
      }

      // Select tool: grab/edit/move an existing inpaint mask before falling back to marquee select.
      if ((isTouch || (event as React.MouseEvent).button === 0) && currentTool === 'canvasControl') {
        const maskWorkspace = findTopWorkspaceAtWorldPosition(worldPos, { annotationsOnly: false });
        if (maskWorkspace) {
          const maskHandle = annotationCanvasRefs.current[maskWorkspace.id];
          const handledByMask = await maskHandle?.handleMouseDown(event);
          if (handledByMask) {
            if (maskWorkspace.id !== kernel.current?.getProjectUIWorkspaceId(projectId)) {
              kernel.current?.setProjectUIWorkspaceId(projectId, maskWorkspace.id);
            }
            activeAnnotationWorkspaceIdRef.current = maskWorkspace.id;
            return;
          }
        }
      }

      if ((isTouch || (event as React.MouseEvent).button === 0) && kernel.current?.getActiveTool() === 'canvasControl') {
        mouseDownTimeRef.current = Date.now();
        canvasControlDragStart.current = worldPos;
        return;
      }

      // If no tool is active and we are inside the frame, start a pan.
      if ((isTouch || (event as React.MouseEvent).button === 0) && kernel.current?.getActiveTool() === null && !kernel.current?.getInventory()?.StudioWorkspace.registry[kernel.current?.getProjectUIWorkspaceId(projectId)]?.isResizingCanvas) {
        startViewportPan(clientX, clientY);
      }

    },
    [
      kernel,
      screenToWorld,
      startViewportPan,
      isNativeLayerCutActive,
      isScissorsWaitingForTarget,
      findTopCuttableLayerAtWorldPosition,
      getWorkspaceStack,
      handleDrawingPointerDown,
      projectId
    ]
  );

  const handleContainerDoubleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    handleDrawingDoubleClick(event);
  }, [handleDrawingDoubleClick]);




  const handleContainerMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      if(!kernel.current) return null;
      const runtimeActiveWorkspaceId = kernel.current.getProjectUIWorkspaceId(projectId);
      const runtimeInventory = kernel.current.getInventory();
      const runtimeActiveWorkspace = runtimeActiveWorkspaceId
        ? runtimeInventory?.StudioWorkspace.registry[runtimeActiveWorkspaceId]
        : null;
      const runtimeFrameResizeState =
        runtimeActiveWorkspace?.frameResizeState?.isResizing
          ? runtimeActiveWorkspace.frameResizeState
          : Object.values(runtimeInventory?.StudioWorkspace.registry ?? {}).find(
              (workspace) => workspace?.frameResizeState?.isResizing === true
            )?.frameResizeState ?? DEFAULT_FRAME_RESIZE_STATE;
      const runtimeCanvasResizeState = runtimeActiveWorkspace?.canvasResizeState ?? DEFAULT_CANVAS_RESIZE_STATE;
      const runtimeIsResizingCanvas = runtimeActiveWorkspace?.isResizingCanvas === true;
      const isTouch = 'touches' in event;
      const clientX = (isTouch ? event.touches[0]?.clientX : event.clientX) || 0;
      const clientY = (isTouch ? event.touches[0]?.clientY : event.clientY) || 0;

      if (handleDrawingPointerMove(event, clientX, clientY, isTouch)) {
        return;
      }

      if (isScissorsWaitingForTarget) {
        const hovered = findTopCuttableLayerAtWorldPosition(screenToWorld(clientX, clientY));
        const nextHoverId = hovered?.layer.id ?? null;
        if (kernel.current.getScissorsHoverLayerId() !== nextHoverId) {
          kernel.current.setScissorsHoverLayerId(nextHoverId);
        }
      }

      const draggedLayerId = getProjectDraggedLayerId(kernel.current, projectId);
      const isResizingLayer = getProjectIsResizingLayer(kernel.current, projectId);
      const pointBeingDragged = getProjectPointBeingDragged(kernel.current, projectId);

      if (kernel.current.getActiveTool() === 'scissors' && !getProjectIsPanning(kernel.current, projectId)) {
        return;
      }

      if (getProjectIsPanning(kernel.current, projectId) && draggedLayerId === null && !isResizingLayer && !pointBeingDragged && !runtimeIsResizingCanvas) {
        const panStartScreen = panStartScreenRef.current ?? mouseDownPos.current;
        const panStartOffset = panStartOffsetRef.current ?? transientPanOffsetRef.current ?? getProjectPanOffset(kernel.current, projectId);
        const dx = clientX - panStartScreen.x;
        const dy = clientY - panStartScreen.y;
        const zoomLevel = getProjectZoomLevel(kernel.current, projectId);
        const newPan = kernel.current.clampViewportPanForProject(projectId, { x: panStartOffset.x + dx, y: panStartOffset.y + dy }, zoomLevel);
        transientPanOffsetRef.current = newPan;
        kernel.current?.applyViewportTransform(newPan, zoomLevel, false);
        return;
      }

      const fRS = runtimeFrameResizeState;
      if (fRS.isResizing) {
        const { workspaceId, handleType, startPos, initialRect } = fRS;
        if (!workspaceId || !handleType || !startPos || !initialRect) return;

        const dx = (clientX - startPos.x) / getProjectZoomLevel(kernel.current, projectId);
        const dy = (clientY - startPos.y) / getProjectZoomLevel(kernel.current, projectId);

        let { x, y, w, h } = initialRect;

        switch (handleType) {
          case 'topLeft':
            w -= dx; x += dx;
            h -= dy; y += dy;
            break;
          case 'topRight':
            w += dx;
            h -= dy; y += dy;
            break;
          case 'bottomLeft':
            w -= dx; x += dx;
            h += dy;
            break;
          case 'bottomRight':
            w += dx;
            h += dy;
            break;
          case 'top':
            h -= dy; y += dy;
            break;
          case 'bottom':
            h += dy;
            break;
          case 'left':
            w -= dx; x += dx;
            break;
          case 'right':
            w += dx;
            break;
          case 'move':
            break;
        }

        // Prevent inversion by enforcing a minimum size
        if (w < 50) {
            if (handleType.includes('left')) x = (initialRect.x + initialRect.w) - 50;
            w = 50;
        }
        if (h < 50) {
            if (handleType.includes('top')) y = (initialRect.y + initialRect.h) - 50;
            h = 50;
        }

        const newFrameRect = { x, y, w, h };

        kernel.current?.setWorkspace(workspaceId, (workspace) => ({
          ...workspace,
          frameRect: newFrameRect,
        }), projectId);
        
        return;
      }

      if (draggedWorkspaceId) {
        const dx = (clientX - mouseDownPos.current.x) / getProjectZoomLevel(kernel.current, projectId);
        const dy = (clientY - mouseDownPos.current.y) / getProjectZoomLevel(kernel.current, projectId);
  
        if (Math.abs(dx) > 0 || Math.abs(dy) > 0) {
          kernel.current?.logUserAction('Workspace Frame Moved', { workspaceId: draggedWorkspaceId, dx, dy });
  
          // Update workspaces (frame and generation rect positions)
          kernel.current?.setWorkspace(draggedWorkspaceId, (workspace) => ({
            ...workspace,
            frameRect: { ...workspace.frameRect, x: workspace.frameRect.x + dx, y: workspace.frameRect.y + dy },
            generationRect: {
              ...workspace.generationRect,
              x: workspace.generationRect.x + dx,
              y: workspace.generationRect.y + dy,
            },
          }), projectId);
  
          mouseDownPos.current = { x: clientX, y: clientY };
        }
        return; // Prevent other mouse move logic
      }

      // --- CONSTRAINED ACTIONS (Must be inside the active frame) ---
      if (kernel.current.getActiveTool() === 'canvasControl' && canvasControlDragStart.current) {
        const currentPoint = screenToWorld(clientX, clientY);
        const x1 = Math.min(canvasControlDragStart.current.x, currentPoint.x);
        const y1 = Math.min(canvasControlDragStart.current.y, currentPoint.y);
        const x2 = Math.max(canvasControlDragStart.current.x, currentPoint.x);
        const y2 = Math.max(canvasControlDragStart.current.y, currentPoint.y);
        if (runtimeActiveWorkspaceId) {
          kernel.current?.setWorkspaceAnimatedGenerationRect(runtimeActiveWorkspaceId,
            { x: x1, y: y1, w: x2 - x1, h: y2 - y1 }
          );
        }
        return; // Prevent other mouse move actions
      }

      const currentTool = kernel.current.getActiveTool();
      const isAnnotationTool =
        currentTool === 'annotationLine' ||
        currentTool === 'annotationEraser' ||
        currentTool === 'comment' ||
        currentTool === 'maskPen';
      const canAnnotationCanvasHandlePointer = isAnnotationTool || currentTool === null || activeAnnotationWorkspaceIdRef.current !== null;
      if (!getProjectIsPanning(kernel.current, projectId) && canAnnotationCanvasHandlePointer) {
        const lockedWorkspaceId = activeAnnotationWorkspaceIdRef.current;
        if (lockedWorkspaceId && currentTool === 'maskPen') {
          const lockedHandle = annotationCanvasRefs.current[lockedWorkspaceId];
          if (!lockedHandle?.hasActiveMaskDraft()) {
            activeAnnotationWorkspaceIdRef.current = null;
          }
        }
        const activeLockedWorkspaceId = activeAnnotationWorkspaceIdRef.current;
        const hoveredWorkspaceId =
          !activeLockedWorkspaceId && isAnnotationTool
            ? findTopWorkspaceAtWorldPosition(screenToWorld(clientX, clientY), { annotationsOnly: currentTool !== 'maskPen' })?.id ?? null
            : null;
        const targetWorkspaceId = activeLockedWorkspaceId ?? hoveredWorkspaceId;
        if (targetWorkspaceId) {
          const handledByMask = annotationCanvasRefs.current[targetWorkspaceId]?.handleMouseMove(event);
          if (handledByMask) return;
        }
      }

      if (getProjectIsPanning(kernel.current, projectId) && draggedLayerId !== null && !isResizingLayer && !pointBeingDragged && !runtimeIsResizingCanvas) {
        const dx = clientX - mouseDownPos.current.x;
        const dy = clientY - mouseDownPos.current.y;

        const selectedIds = getProjectSelectedLayersId(kernel.current, projectId);
        const isSelectionDrag = draggedLayerId ? selectedIds.includes(draggedLayerId) : false;
        const layersToMoveIds = draggedLayerId
          ? (isSelectionDrag ? selectedIds : [draggedLayerId])
          : [];

        const deltaX = dx / getProjectZoomLevel(kernel.current, projectId);
        const deltaY = dy / getProjectZoomLevel(kernel.current, projectId);
        layersToMoveIds.forEach((layerId) => {
          const layer = kernel.current?.getLayerRegistry()[layerId];
          if (!layer) return;
          kernel.current?.setLayer(layer.id, (existingLayer) => ({
            ...existingLayer,
            ...layer,
            rect: { ...layer.rect, x: layer.rect.x + deltaX, y: layer.rect.y + deltaY },
            revealCutoffX: null,
          }));
        });
        mouseDownPos.current = { x: clientX, y: clientY };
      } else if (isResizingLayer && pointBeingDragged && !runtimeIsResizingCanvas) {
        const { layerId, pointName } = pointBeingDragged;
        const layer = kernel.current.getLayerRegistry()[layerId];
        const workspace = layer?.layerWorkspaceId ? kernel.current?.getWorkspace(layer.layerWorkspaceId) : null;

        const layerRendersAsSvg = isLayerVectorized(layer);
        if (layer && workspace && (layer.imageData || layerRendersAsSvg) && kernel.current?.canvasContainerRef.current) {
          const containerRect = kernel.current.canvasContainerRef.current.getBoundingClientRect();
          
          const currentPanOffset = getProjectPanOffset(kernel.current, projectId);
          const currentZoomLevel = getProjectZoomLevel(kernel.current, projectId);
          const mouseX = (clientX - containerRect.left - currentPanOffset.x) / currentZoomLevel;
          const mouseY = (clientY - containerRect.top - currentPanOffset.y) / currentZoomLevel;

          if (layerRendersAsSvg) {
            if (!resizeStart.current?.initialRect) return;

            const dx = (clientX - resizeStart.current.x) / getProjectZoomLevel(kernel.current, projectId);
            const dy = (clientY - resizeStart.current.y) / getProjectZoomLevel(kernel.current, projectId);

            const initialRect = resizeStart.current.initialRect;
            let { x, y, w, h } = initialRect;

            switch (pointName) {
              case 'topLeft': w -= dx; x += dx; h -= dy; y += dy; break;
              case 'topRight': w += dx; h -= dy; y += dy; break;
              case 'bottomLeft': w -= dx; x += dx; h += dy; break;
              case 'bottomRight': w += dx; h += dy; break;
              case 'top': h -= dy; y += dy; break;
              case 'bottom': h += dy; break;
              case 'left': w -= dx; x += dx; break;
              case 'right': w += dx; break;
            }

            // Hold Shift to resize uniformly: lock the original aspect ratio and keep the
            // opposite corner/edge anchored (free-form resize otherwise).
            const shiftHeld = !isTouch && Boolean((event as { shiftKey?: boolean }).shiftKey);
            if (shiftHeld && initialRect.w > 0 && initialRect.h > 0) {
              const ar = initialRect.w / initialRect.h;
              const right = initialRect.x + initialRect.w;
              const bottom = initialRect.y + initialRect.h;
              if (pointName === 'topLeft' || pointName === 'topRight' || pointName === 'bottomLeft' || pointName === 'bottomRight') {
                if (w / ar >= h) { h = w / ar; } else { w = h * ar; }
                switch (pointName) {
                  case 'topLeft': x = right - w; y = bottom - h; break;
                  case 'topRight': x = initialRect.x; y = bottom - h; break;
                  case 'bottomLeft': x = right - w; y = initialRect.y; break;
                  case 'bottomRight': x = initialRect.x; y = initialRect.y; break;
                }
              } else if (pointName === 'left' || pointName === 'right') {
                h = w / ar;
                y = initialRect.y + (initialRect.h - h) / 2;
              } else {
                w = h * ar;
                x = initialRect.x + (initialRect.w - w) / 2;
              }
            }

            if (w < 20) {
              if (pointName.includes('left')) x = (initialRect.x + initialRect.w) - 20;
              w = 20;
            }
            if (h < 20) {
              if (pointName.includes('top')) y = (initialRect.y + initialRect.h) - 20;
              h = 20;
            }

            kernel.current?.setLayer(layerId, (currentLayer) => ({ ...currentLayer, rect: { x, y, w, h } }));
          } else if (layer.imageData) {
            const originalWidth = layer.imageData.width;
            const originalHeight = layer.imageData.height;

            const layerWorldX = workspace.frameRect.x + layer.rect.x;
            const layerWorldY = workspace.frameRect.y + layer.rect.y;
            
            let newWorldX = layerWorldX;
            let newWorldY = layerWorldY;
            let newScale = layer.scale || 1;

            const oldScaledWidth = originalWidth * newScale;
            const oldScaledHeight = originalHeight * newScale;

            if (pointName === 'topLeft' || pointName === 'topRight' || pointName === 'bottomLeft' || pointName === 'bottomRight') {
              const aspectRatio = originalWidth / originalHeight;
              let anchorX, anchorY;

              switch (pointName) {
                case 'topLeft': anchorX = layerWorldX + oldScaledWidth; anchorY = layerWorldY + oldScaledHeight; break;
                case 'topRight': anchorX = layerWorldX; anchorY = layerWorldY + oldScaledHeight; break;
                case 'bottomLeft': anchorX = layerWorldX + oldScaledWidth; anchorY = layerWorldY; break;
                case 'bottomRight': anchorX = layerWorldX; anchorY = layerWorldY; break;
              }

              let newWidth = Math.abs(mouseX - anchorX!);
              let newHeight = Math.abs(mouseY - anchorY!);

              if (newWidth / aspectRatio > newHeight) { newHeight = newWidth / aspectRatio; } else { newWidth = newHeight * aspectRatio; }

              newScale = newWidth / originalWidth;

              switch (pointName) {
                case 'topLeft': newWorldX = anchorX! - newWidth; newWorldY = anchorY! - newHeight; break;
                case 'topRight': newWorldX = anchorX!; newWorldY = anchorY! - newHeight; break;
                case 'bottomLeft': newWorldX = anchorX! - newWidth; newWorldY = anchorY!; break;
                case 'bottomRight': newWorldX = anchorX!; newWorldY = anchorY!; break;
              }
            } else { // Side handles for proportional resize
              switch (pointName) {
                case 'right': {
                  const newWidth = mouseX - layerWorldX;
                  newScale = newWidth / originalWidth;
                  const newHeight = originalHeight * newScale;
                  newWorldY = layerWorldY - (newHeight - oldScaledHeight) / 2;
                  break;
                }
                case 'left': {
                  const rightEdge = layerWorldX + oldScaledWidth;
                  const newWidth = rightEdge - mouseX;
                  newScale = newWidth / originalWidth;
                  const newHeight = originalHeight * newScale;
                  newWorldX = rightEdge - (originalWidth * newScale);
                  newWorldY = layerWorldY - (newHeight - oldScaledHeight) / 2;
                  break;
                }
                case 'bottom': {
                  const newHeight = mouseY - layerWorldY;
                  newScale = newHeight / originalHeight;
                  const newWidth = originalWidth * newScale;
                  newWorldX = layerWorldX - (newWidth - oldScaledWidth) / 2;
                  break;
                }
                case 'top': {
                  const bottomEdge = layerWorldY + oldScaledHeight;
                  const newHeight = bottomEdge - mouseY;
                  newScale = newHeight / originalHeight;
                  const newWidth = originalWidth * newScale;
                  newWorldY = bottomEdge - (originalHeight * newScale);
                  newWorldX = layerWorldX - (newWidth - oldScaledWidth) / 2;
                  break;
                }
              }
            }

            const newRelativeX = newWorldX - workspace.frameRect.x;
            const newRelativeY = newWorldY - workspace.frameRect.y;
            const newWorldWidth = originalWidth * newScale;
            const newWorldHeight = originalHeight * newScale;

            kernel.current?.setLayer(layerId, (currentLayer) => ({
              ...currentLayer,
              scale: newScale,
              rect: { ...currentLayer.rect, x: newRelativeX, y: newRelativeY, w: newWorldWidth, h: newWorldHeight },
              revealCutoffX: null,
            }));
          }
        }
      } else if (runtimeIsResizingCanvas && runtimeCanvasResizeState.isResizing && runtimeCanvasResizeState.startPos && runtimeCanvasResizeState.handleType) {
        const dx = (clientX - runtimeCanvasResizeState.startPos.x) / getProjectZoomLevel(kernel.current, projectId);
        const dy = (clientY - runtimeCanvasResizeState.startPos.y) / getProjectZoomLevel(kernel.current, projectId);
        const targetWorkspaceId = runtimeCanvasResizeState.workspaceId || runtimeActiveWorkspaceId;
        if (targetWorkspaceId) {
          kernel.current?.setWorkspace(targetWorkspaceId, (workspace) => ({
            ...workspace,
            isResizingCanvas: true,
            canvasResizeState: {
              ...(workspace.canvasResizeState ?? DEFAULT_CANVAS_RESIZE_STATE),
              delta: { dx, dy },
              isShiftPressed: event.shiftKey,
            },
          }), projectId);
        }
      }
    },
    [
      draggedWorkspaceId,
      kernel,
      screenToWorld,
      isScissorsWaitingForTarget,
      findTopCuttableLayerAtWorldPosition,
      handleDrawingPointerMove,
      projectId
    ]
  );

  const handleContainerMouseUp = useCallback((event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    // Clé volontairement `activeWorkspaceId` et non `getProjectUIWorkspaceId(projectId)` comme dans
    // handleContainerMouseMove : les deux replis divergent ('1' ici, activeLayerWorkspaceId là-bas).
    const runtimeActiveWorkspace = activeWorkspaceId
      ? kernel.current?.getInventory()?.StudioWorkspace.registry[activeWorkspaceId]
      : null;
    const runtimeFrameResizeState = runtimeActiveWorkspace?.frameResizeState ?? DEFAULT_FRAME_RESIZE_STATE;
    const runtimeCanvasResizeState = runtimeActiveWorkspace?.canvasResizeState ?? DEFAULT_CANVAS_RESIZE_STATE;
    const runtimeIsResizingCanvas = runtimeActiveWorkspace?.isResizingCanvas === true;
    const isTouch = 'touches' in event;
    const clientX = (isTouch ? (event as React.TouchEvent).changedTouches[0]?.clientX : (event as React.MouseEvent).clientX) || 0;
    const clientY = (isTouch ? (event as React.TouchEvent).changedTouches[0]?.clientY : (event as React.MouseEvent).clientY) || 0;

    if (handleDrawingPointerUp(event, clientX, clientY)) {
      return;
    }

    if (dragOperationStateRef.current && kernel.current) {
      const { type, initialLayers, reclick } = dragOperationStateRef.current;
      const finalLayers = initialLayers
        .map((initialLayer) => kernel.current?.getLayerRegistry()[initialLayer.id])
        .filter((layer): layer is Layer => Boolean(layer))
        .map((layer) => {
          const snapshot = cloneLayerSnapshot(layer);
          return type === 'resize'
            ? (rerenderTextDrawingLayerAtCurrentSize(snapshot) ?? snapshot)
            : snapshot;
        });

      if (finalLayers.length === initialLayers.length) {
        const MOVE_THRESHOLD = 1;
        const SCALE_THRESHOLD = 0.001;
        const finalLayersMap = new Map(finalLayers.map(l => [l.id, l]));
        const initialLayersMap = new Map(initialLayers.map(l => [l.id, l]));
        const logEntriesMap = new Map<string, LayerModificationLogEntry[]>();

        const hasChanged = initialLayers.some(initialLayer => {
          const finalLayer = finalLayersMap.get(initialLayer.id);
          if (!finalLayer) return false;
          const moveDeltaX = Math.abs(initialLayer.rect.x - finalLayer.rect.x);
          const moveDeltaY = Math.abs(initialLayer.rect.y - finalLayer.rect.y);
          const moved = moveDeltaX > MOVE_THRESHOLD || moveDeltaY > MOVE_THRESHOLD;
          const initialScale = initialLayer.scale ?? 1;
          const finalScale = finalLayer.scale ?? 1;
          const scaleChanged = Math.abs(initialScale - finalScale) > SCALE_THRESHOLD;
          const widthChanged = Math.abs((initialLayer.rect.w ?? 0) - (finalLayer.rect.w ?? 0)) > MOVE_THRESHOLD;
          const heightChanged = Math.abs((initialLayer.rect.h ?? 0) - (finalLayer.rect.h ?? 0)) > MOVE_THRESHOLD;
          const imageChanged = initialLayer.imageData !== finalLayer.imageData;
          const paramsChanged = initialLayer.params !== finalLayer.params;
          const resized = scaleChanged || widthChanged || heightChanged || imageChanged || paramsChanged;

          const entries: LayerModificationLogEntry[] = [];
          if (moved) {
            entries.push({ timestamp: new Date().toISOString(), type: 'MANUAL_MOVE' });
          }
          if (resized && type === 'resize') {
            entries.push({ timestamp: new Date().toISOString(), type: 'MANUAL_RESIZE' });
          }
          if (entries.length > 0) {
            logEntriesMap.set(initialLayer.id, entries);
          }

          return moved || resized;
        });

        if (reclick && isStationaryLayerReclick(reclick, { x: clientX, y: clientY }, hasChanged)) {
          requestProjectLayerListReveal(kernel.current, projectId, reclick.layerId);
          kernel.current.bumpLayerPulseToken(reclick.layerId);
        }

        if (hasChanged) {
          const description = type === 'move'
            ? `Move Layer${initialLayers.length > 1 ? 's' : ''}`
            : `Resize Layer`;

          kernel.current.executeAction({
            description,
            undo: () => {
              initialLayersMap.forEach((initialState, layerId) => {
                kernel.current?.setLayer(layerId, (layer) => ({
                  ...layer,
                  ...initialState,
                  rect: { ...initialState.rect },
                  scale: initialState.scale,
                  modificationLog: initialState.modificationLog
                    ? initialState.modificationLog.map((entry) => ({ ...entry }))
                    : undefined,
                }));
              });
            },
            redo: () => {
              finalLayersMap.forEach((finalState, layerId) => {
                kernel.current?.setLayer(layerId, (layer) => {
                  const logEntries = logEntriesMap.get(layerId);
                  const existingLog = finalState.modificationLog ?? [];
                  return {
                    ...layer,
                    ...finalState,
                    rect: { ...finalState.rect },
                    scale: finalState.scale,
                    modificationLog: logEntries && logEntries.length > 0
                      ? [...existingLog, ...logEntries]
                      : existingLog.length > 0
                        ? [...existingLog]
                        : undefined,
                  };
                });
              });
            }
          });
        }
      }
      dragOperationStateRef.current = null;
    }

    if (draggedWorkspaceId) setDraggedWorkspaceId(null);
    
    if (canvasControlDragStart.current) {
      const setGenerationArea = kernel.current?.setGenerationArea;
  
      // Always treat as a drag. A click will result in a zero-size rect, which is filtered out.
      const workspaceId = kernel.current?.getProjectUIWorkspaceId(projectId) || '';
      let aGR = workspaceId
        ? (kernel.current?.getWorkspace(workspaceId)?.animatedGenerationRect ?? null)
        : null;
      if (setGenerationArea && aGR && aGR.w > 1 && aGR.h > 1) {
        const previewRect = { ...aGR };
        const shouldAnimateSnap =
          kernel.current?.getSpectacleMode() === true &&
          workspaceId.length > 0;

        if (shouldAnimateSnap) {
          kernel.current?.setWorkspaceAnimatedGenerationRect(workspaceId, previewRect);
        }

        void (async () => {
          try {
            await setGenerationArea(workspaceId, previewRect.x, previewRect.y, previewRect.w, previewRect.h);

            if (shouldAnimateSnap) {
              const appliedWorkspace = kernel.current?.getWorkspace(workspaceId);
              const appliedRect = appliedWorkspace?.generationRect;

              if (appliedRect) {
                const delta =
                  Math.abs(previewRect.x - appliedRect.x) +
                  Math.abs(previewRect.y - appliedRect.y) +
                  Math.abs(previewRect.w - appliedRect.w) +
                  Math.abs(previewRect.h - appliedRect.h);

                if (delta > 0.01) {
                  const animationProps = { ...previewRect };
                  await anime({
                    targets: animationProps,
                    x: appliedRect.x,
                    y: appliedRect.y,
                    w: appliedRect.w,
                    h: appliedRect.h,
                    easing: 'easeInOutSine',
                    duration: 220,
                    update: () => {
                      kernel.current?.setWorkspaceAnimatedGenerationRect(workspaceId, {
                        x: animationProps.x,
                        y: animationProps.y,
                        w: animationProps.w,
                        h: animationProps.h,
                      });
                    },
                  }).finished;
                }
              }
            }
          } finally {
            kernel.current?.setWorkspaceAnimatedGenerationRect(workspaceId, null);
          }
        })();
      }
  
      canvasControlDragStart.current = null;
      return;
    }

    if (kernel.current && getProjectIsPanning(kernel.current, projectId)) {
      const finalPan = transientPanOffsetRef.current;
      if (finalPan) {
        kernel.current?.applyViewportTransform(finalPan, getProjectZoomLevel(kernel.current, projectId), true);
        setProjectPanOffset(kernel.current, projectId, finalPan);
      }
      transientPanOffsetRef.current = null;
      panStartScreenRef.current = null;
      panStartOffsetRef.current = null;
      setProjectIsPanning(kernel.current, projectId, false);

      const duration = Date.now() - mouseDownTimeRef.current;
      const movedX = Math.abs(clientX - mouseDownPos.current.x);
      const movedY = Math.abs(clientY - mouseDownPos.current.y);
      const moved = movedX > 0 || movedY > 0;

      const isFastClick = duration < 200;

      const worldPos = screenToWorld(clientX, clientY);
      const activeWorkspaceId = kernel.current?.getProjectUIWorkspaceId(projectId);
      const activeWorkspace = activeWorkspaceId ? kernel.current?.getWorkspace(activeWorkspaceId) : null;
      const frame = activeWorkspace?.frameRect;
      const isOnActiveWorkspace = frame &&
        worldPos.x >= frame.x && worldPos.x <= frame.x + frame.w &&
        worldPos.y >= frame.y && worldPos.y <= frame.y + frame.h;

      if (!moved && isFastClick && isOnActiveWorkspace) {
        if (kernel.current && activeWorkspace) {
          selectLayerAtWorldPosition(activeWorkspace, worldPos, event.shiftKey);
        }
      }
    }

    const annotationWorkspaceId = activeAnnotationWorkspaceIdRef.current;
    if (annotationWorkspaceId) {
      const annotationHandle = annotationCanvasRefs.current[annotationWorkspaceId];
      annotationHandle?.handleMouseUp(event);
      if (!annotationHandle?.hasActiveMaskDraft()) {
        activeAnnotationWorkspaceIdRef.current = null;
      }
    }

    const fRS = runtimeFrameResizeState;
    if (fRS && fRS.isResizing && fRS.workspaceId !== null) {
      if (fRS.workspaceId) {
        const resizedWorkspace = kernel.current?.getWorkspace(fRS.workspaceId);
        if (resizedWorkspace && fRS.initialRect) {
          const oldFrameRect = fRS.initialRect;
          const newFrameRect = resizedWorkspace.frameRect;
          
          const deltaX = newFrameRect.x - oldFrameRect.x;
          const deltaY = newFrameRect.y - oldFrameRect.y;

          // Apply the final position update to layers once on mouse up.
          if (Math.abs(deltaX) > 0.01 || Math.abs(deltaY) > 0.01) {
            getWorkspaceOwnedLayers(fRS.workspaceId).forEach((layer) => {
              kernel.current?.setLayer(layer.id, {
                ...layer,
                rect: { ...layer.rect, x: layer.rect.x - deltaX, y: layer.rect.y - deltaY },
              });
            });
          }

          kernel.current?.setWorkspaceFrameRect(
             fRS.workspaceId, 
             newFrameRect, 
             oldFrameRect // <--- Passed here
          );

          // Clamp canvas rect
          const canvas = resizedWorkspace.generationRect;
          const newCanvasX = Math.max(newFrameRect.x, canvas.x);
          const newCanvasY = Math.max(newFrameRect.y, canvas.y);
          const newCanvasMaxX = Math.min(newFrameRect.x + newFrameRect.w, canvas.x + canvas.w);
          const newCanvasMaxY = Math.min(newFrameRect.y + newFrameRect.h, canvas.y + canvas.h);
          const newCanvasW = Math.max(1, newCanvasMaxX - newCanvasX);
          const newCanvasH = Math.max(1, newCanvasMaxY - newCanvasY);
          
          (async () => {
            await kernel.current?.setGenerationArea(fRS.workspaceId!, newCanvasX, newCanvasY, newCanvasW, newCanvasH, true);
          })();

          kernel.current?.logUserAction('Workspace Frame Resized', { 
            workspaceId: fRS.workspaceId, 
            finalFrameRect: newFrameRect 
          });
        }
      }
      kernel.current?.setWorkspace(fRS.workspaceId, (workspace) => ({
        ...workspace,
        frameResizeState: DEFAULT_FRAME_RESIZE_STATE,
      }), projectId);
    }

    const runtimeKernel = kernel.current;
    if (runtimeKernel) {
      setProjectPointBeingDragged(runtimeKernel, projectId, null);
      if (getProjectIsResizingLayer(runtimeKernel, projectId)) {
        setProjectIsResizingLayer(runtimeKernel, projectId, false);
        resizeStart.current = null;
      }
    }
    
    if (runtimeIsResizingCanvas && runtimeCanvasResizeState.isResizing && kernel.current) {
      const resizedWorkspaceId = runtimeCanvasResizeState.workspaceId;
      if (!resizedWorkspaceId) {
        console.error("Resize ended but no workspaceId in canvasResizeState");
        if (activeWorkspaceId) {
          kernel.current?.setWorkspace(activeWorkspaceId, (workspace) => ({
            ...workspace,
            isResizingCanvas: false,
            canvasResizeState: DEFAULT_CANVAS_RESIZE_STATE,
          }), projectId);
        }
        return;
      }

      const resizedWorkspace = kernel.current?.getWorkspace(resizedWorkspaceId);
      if (!resizedWorkspace) {
        console.error(`Could not find workspace with id ${resizedWorkspaceId} to end resize.`);
        kernel.current?.setWorkspace(resizedWorkspaceId, (workspace) => ({
          ...workspace,
          isResizingCanvas: false,
          canvasResizeState: DEFAULT_CANVAS_RESIZE_STATE,
        }), projectId);
        return;
      }

      const activeWorkspaceIdForResize = kernel.current?.getProjectUIWorkspaceId(projectId);
      const aspectRatioForResize = (resizedWorkspaceId === activeWorkspaceIdForResize)
        ? (activeWorkspaceIdForResize ? (kernel.current?.getWorkspace(activeWorkspaceIdForResize)?.aspectRatio || null) : null)
        : null;

      const newRect = kernel.current?.calculateNewRectForResize(runtimeCanvasResizeState, resizedWorkspace.generationRect, aspectRatioForResize, resizedWorkspace.frameRect);
      kernel.current?.setGenerationArea(resizedWorkspaceId, newRect.x, newRect.y, newRect.w, newRect.h);
      kernel.current?.setWorkspace(resizedWorkspaceId, (workspace) => ({
        ...workspace,
        isResizingCanvas: false,
        canvasResizeState: DEFAULT_CANVAS_RESIZE_STATE,
      }), projectId);
    }
    if (runtimeKernel) {
      setProjectDraggedLayerId(runtimeKernel, projectId, null);
    }
  }, [
    kernel,
    draggedWorkspaceId,
    screenToWorld,
    handleDrawingPointerUp,
    rerenderTextDrawingLayerAtCurrentSize,
    selectLayerAtWorldPosition,
    getWorkspaceOwnedLayers,
    projectId,
    activeWorkspaceId,
  ]);

  const handleContainerMouseLeave = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (kernel.current?.getActiveTool() === 'scissors' && !kernel.current.getNativeLayerCutSession()) {
      kernel.current.setScissorsHoverLayerId(null);
    }
    handleContainerMouseUp(event);
  }, [kernel, handleContainerMouseUp]);


  useEffect(() => {
    const canvasContainer = kernel.current?.canvasContainerRef.current;
    if (!canvasContainer) return;

    const wheelHandler = (event: WheelEvent) => {
      // The docked timeline scrolls its own lanes; a wheel over it never zooms the canvas,
      // and a pinch (ctrl+wheel) over it must not zoom the browser page either.
      if (event.target instanceof Element && event.target.closest('[data-timeline-dock]')) {
        if (event.ctrlKey || event.metaKey) event.preventDefault();
        return;
      }
      handleWheel(event as unknown as React.WheelEvent<HTMLDivElement>);
    };
    const contextMenuHandler = (event: Event) => {
      event.preventDefault();
    };

    canvasContainer.addEventListener('wheel', wheelHandler, { passive: false });
    canvasContainer.addEventListener('contextmenu', contextMenuHandler);

    return () => {
      canvasContainer.removeEventListener('wheel', wheelHandler);
      canvasContainer.removeEventListener('contextmenu', contextMenuHandler);
    };
  }, [handleWheel, kernel]);

  useEffect(() => {
    if (!kernel.current) return;

    const initialPan = getProjectPanOffset(kernel.current, projectId);
    const initialZoom = getProjectZoomLevel(kernel.current, projectId);
    kernel.current.applyViewportTransform(initialPan, initialZoom);
  }, [kernel, projectId]);


  useEffect(() => {
    const container = kernel.current?.canvasContainerRef.current;
    if (!container) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      kernel.current?.handleCanvasKeyDown(event);
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      };
  }, [kernel]);

  const memoizedSceneContent = useMemo(() => {
    return (
      <>
        {mountedWorkspaceIds.map((workspaceId) => {
          return (
            <WorkspaceCard
              key={workspaceId}
              projectId={projectId}
              workspaceId={workspaceId}
              mouseDownPos={mouseDownPos}
              resizeStart={resizeStart}
              onDragStart={onDragStart}
              registerAnnotationCanvasRef={registerAnnotationCanvasRef}
              onEditDrawingTextLayer={startEditingDrawingTextLayer}
              editingDrawingTextLayerId={textDrawingDraft?.editingLayerId ?? null}
            />
          );
        })}

        <GeneratingLayers projectId={projectId} />
      </>
    );
  }, [
    mountedWorkspaceIds,
    projectId,
    onDragStart,
    registerAnnotationCanvasRef,
    startEditingDrawingTextLayer,
    textDrawingDraft?.editingLayerId,
  ]);

  const { canvasClassName, canvasStyle } = useMemo(() => {
    const isPanDrag = activeTool === null && isPanning;
    const cursor = draggedWorkspaceId
      ? 'grabbing'
      : activeTool === 'maskPen'
        ? getMaskPenCursor(maskPenMode)
        : activeTool === 'scissors'
          ? scissorsCursor
          : activeTool === 'canvasControl'
            ? generationAreaCursor
            : activeTool === 'draw'
              ? 'crosshair'
              : (isPanDrag || isResizingCanvas)
              ? 'grab'
              : 'default';
    // A layer or workspace frame under the pointer sets its own cursor, which would hide this one.
    const override = draggedWorkspaceId
      ? ' [&_*]:cursor-grabbing!'
      : isPanDrag
        ? ' [&_*]:cursor-grab!'
        : '';
    return {
      canvasClassName: `relative flex overflow-hidden${override}`,
      canvasStyle: {
        height: '100%',
        cursor,
        // A pan's mouse drag would otherwise open an invisible selection, and the next drag inside it starts a native drag.
        userSelect: 'none',
        touchAction: 'none',
        overscrollBehavior: 'none',
        backgroundColor: DEFAULT_BACKDROP_THEME.baseColor,
      } satisfies React.CSSProperties,
    };
  }, [activeTool, draggedWorkspaceId, isPanning, isResizingCanvas, maskPenMode]);

  const ideogramPromptAvatar = useMemo(
    () => ({ imageUrl: currentUserAvatarUrl, alt: 'You', color: '#0D99FF' }),
    [currentUserAvatarUrl]
  );

  const ideogramPromptLeft = ideogramInpaintPrompt
    ? Math.min(Math.max(8, ideogramInpaintPrompt.canvasX + 10), Math.max(8, viewportSize.width - 260))
    : 0;
  const ideogramPromptTop = ideogramInpaintPrompt
    ? Math.min(Math.max(8, ideogramInpaintPrompt.canvasY + 10), Math.max(8, viewportSize.height - 56))
    : 0;

  return (
    <div
      id="editor-canvas"
      data-project-id={projectId || undefined}
      className={canvasClassName}
      style={canvasStyle}
      onMouseDown={handleContainerMouseDown}
      onTouchStart={handleContainerMouseDown}
      onMouseMove={handleContainerMouseMove}
      onTouchMove={handleContainerMouseMove}
      onMouseUp={handleContainerMouseUp}
      onTouchEnd={handleContainerMouseUp}
      onDoubleClick={handleContainerDoubleClick}
      onContextMenu={handleCanvasContextMenu}
      onMouseLeave={handleContainerMouseLeave}
      ref={kernel.current?.canvasContainerRef}
      tabIndex={0}
      drop-area="project"
    >
      <div className="absolute inset-0" style={STUDIO_LIVE_VIEWPORT_VARS_RESET}>
        <SubtleEnergyBackground
          ref={starfieldRef}
          projectId={projectId}
        />

        <div 
          ref={transformContainerRef} 
          data-viewport-transform-container="true"
          className="absolute inset-0" 
          style={{ transformOrigin: '0 0', transition: 'none' }}
        >
          {memoizedSceneContent}
          <CanvasDrawingToolOverlay
            shapeDraft={shapeDrawingDraft}
            pencilDraft={pencilDrawingDraft}
            polygonDraft={polygonDrawingDraft}
            textDraft={textDrawingDraft}
            drawingStyle={drawingStyle}
            onTextDraftChange={handleTextDraftChange}
            onTextBlur={handleTextDrawingBlur}
            onTextCancel={cancelTextDrawing}
            onTextCommit={commitTextDrawingDraft}
          />
        </div>
      </div>

      {isColumnPickOpen && <WorkspaceColumnPickOverlay />}
      {isRecastZonePickOpen && <RecastZonePickOverlay />}

      {ideogramInpaintPrompt && (
        <FloatingCanvasInput
          value={ideogramInpaintPrompt.prompt}
          placeholder={t.inpaintPromptPlaceholder}
          submitTitle={t.inpaint}
          avatar={ideogramPromptAvatar}
          left={ideogramPromptLeft}
          top={ideogramPromptTop}
          onChange={changeIdeogramInpaintPrompt}
          onSubmit={submitIdeogramInpaintPrompt}
          onCancel={cancelIdeogramInpaintPrompt}
        />
      )}

      <NewInpaintToolbar
        projectId={projectId}
        openDrawingPanelRequestToken={drawingPanelRequestToken}
      />

      {gridGenerationPanelState && (
        <GridGenerationInlinePanel
          key={gridGenerationPanelState.openedAt}
          projectId={projectId}
          workspaceId={gridGenerationPanelState.workspaceId}
          payload={gridGenerationPanelState.payload}
          onClose={closeGridGenerationPanel}
        />
      )}

      <BottomBar projectId={projectId} />
    </div>
  );
});

InpaintCanvas.displayName = 'InpaintCanvas';
export default InpaintCanvas;
